import { useCallback, useEffect, useState } from "react";
import { AppPlan } from "models/interfaces";
import { fetchAccountDetails, fetchPlansCatalog } from "Services/CommonServices";
import { getSeatUsage, isPerSeatPlan, SeatUsage } from "helpers/perSeatHelpers";
import useBillingConfig from "hooks/use-billing-config";
import strings from "global/constants/StringConstants";
import { store } from "utils/store";

export interface SeatContext {
  /** Live seat usage from `GET /secure/billing/fetch/planLimit`. */
  seats: SeatUsage;
  /**
   * Whether seats are sold on this account — true only when **both** gates
   * agree: the app's `perSeatBillingEnabled` flag and the plan's own
   * `metadata.perSeatBilling`. Either one off means flat billing, and the
   * backend would ignore any seat action the UI offered.
   */
  isPerSeat: boolean;
  /** The app-level gate on its own, for screens that need it separately. */
  isPerSeatApp: boolean;
  /** The catalog entry for the plan the account is on, when it was matched. */
  catalogPlan?: AppPlan;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const emptyUsage = getSeatUsage(undefined);

const matchesPlanId = (plan: AppPlan, currentPlan: any) =>
  Boolean(plan?.id) && Boolean(currentPlan?.id) && plan.id === currentPlan.id;

const matchesPlanName = (plan: AppPlan, currentPlan: any) => {
  const planName = String(plan?.name ?? "").trim().toLowerCase();
  const currentName = String(currentPlan?.name ?? "").trim().toLowerCase();
  return Boolean(planName) && planName === currentName;
};

/**
 * The catalog carries a monthly *and* an annual row per plan name, so the id
 * has to be tried across the whole list before falling back to the name —
 * matching either way per row would let "Standard" monthly answer for an
 * account on "Standard" annual, and quote that row's price.
 */
const findCurrentPlan = (plans: AppPlan[], currentPlan: any) =>
  plans.find((plan) => matchesPlanId(plan, currentPlan)) ??
  plans.find((plan) => matchesPlanName(plan, currentPlan));

/**
 * Seat usage plus the per-seat flag for the account's current plan.
 *
 * Screens need both: the meter to show seats used of seats bought, and the flag
 * to decide whether the account can buy seats at all. Pass the current plan
 * (from `AccountContext` or the plan API) so it can be matched against the
 * catalog, which is where `metadata.perSeatBilling` lives.
 */
export const useSeatContext = (currentPlan?: any): SeatContext => {
  const [seats, setSeats] = useState<SeatUsage>(emptyUsage);
  const [catalogPlan, setCatalogPlan] = useState<AppPlan | undefined>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const { perSeatBillingEnabled } = useBillingConfig();

  const load = useCallback(async () => {
    // Auth state carries no app name, so the product constant is the source.
    const app = store.getState().auth.app || strings.inkfree;
    try {
      setLoading(true);
      // The catalog supplies the current plan's `metadata.perSeatBilling`, its
      // seat cap and its price — everything the seat card and the manage-seats
      // dialog quote. The meter itself comes from the planLimit call alongside.
      const [planLimit, plans] = await Promise.all([
        fetchAccountDetails(),
        fetchPlansCatalog(app),
      ]);
      setSeats(getSeatUsage(planLimit));
      setCatalogPlan(findCurrentPlan(plans, currentPlan));
    } catch {
      // Seat data is supporting information — a failure must not take the
      // screen down. The meter simply stays at its last known values.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan?.id, currentPlan?.name]);

  useEffect(() => {
    load();
  }, [load]);

  // Both gates, as the backend applies them. The plan flag falls back to the
  // account's own plan, which carries `metadata` too when the catalog missed.
  const isPerSeatPlanOn =
    isPerSeatPlan(catalogPlan) || isPerSeatPlan(currentPlan);

  return {
    seats,
    isPerSeat: perSeatBillingEnabled && isPerSeatPlanOn,
    isPerSeatApp: perSeatBillingEnabled,
    catalogPlan,
    isLoading,
    refresh: load,
  };
};

export default useSeatContext;
